import SHA256 from 'crypto-js/sha256'
import encBase64 from 'crypto-js/enc-base64'
import { PushNotification } from '~/constants/push-notification';

export function onlyAllowNumbers(e: KeyboardEvent): void {
  const char = String.fromCharCode(e.keyCode);
  if (!/[0-9]/.test(char)) {
    e.preventDefault();
  }
}
export function isValidValue(val: any) {
  return val !== undefined && val !== null && val !== '' && val !== false;
}

export function isSameDate(a: string | null, b: string | null) {
  if (!a || !b) return false;

  const dateA = new Date(a);
  const dateB = new Date(b);

  return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth() && dateA.getDate() === dateB.getDate();
}

export function isMonthMatch(month: string, applyDate: string): boolean {
  if (!month || !applyDate) return false;
  const date = new Date(applyDate);
  if (isNaN(date.getTime())) return false;
  const applyMonth = date.getMonth() + 1;
  return parseInt(month) === applyMonth;
}

export function getContrastingColor(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
}

export function isNumber(value: string): boolean {
  return value ? /^[0-9]+$/.test(value) : true;
}

export function isValidNumber(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value === 'string' && value.trim() === '') return false;
  return !isNaN(Number(value));
}

export function getFileName(response: any) {
  const contentFileName = response.headers.get('Content-File-Name');
  const fileNameMatch = contentFileName.match(/filename\*?=(?:UTF-8'')?([^;]+)/);
  const fileName = decodeURIComponent(fileNameMatch[1].replace(/"/g, ''));
  return fileName;
}

export function copyTextToClipboard(textToCopy: string, elementParentDom: any, successCallback: any, errorCallback: any) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(textToCopy).then(
      (success) => successCallback(),
      (error) => errorCallback(error)
    );
  } else {
    if (elementParentDom) {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      elementParentDom.appendChild(textarea);

      textarea.select();
      const successful = document.execCommand('copy');

      successful ? successCallback() : errorCallback();
      elementParentDom.removeChild(textarea);
    }
  }
}

export function createHeaderTable(
  headers: {
    title: string;
    key: string;
    headerProps?: object;
    color?: string;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    cursor?: string;
  }[]
) {
return headers.map((header) => ({
    title: header.title,
    key: header.key,
    sortable: false,
    headerClass: 'custom-header',
    color: header.color || '',
    width: header.width || '',
    minWidth: header.minWidth || '',
    maxWidth: header.maxWidth || '',
    cursor: header.cursor || '',
    headerProps: header.headerProps
}));
}

export function generateCodeVerifier(length = 64) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
}

export function generateCodeChallenge(verifier: string): string {
  const hash = SHA256(verifier)                         
  const base64 = encBase64.stringify(hash)              
  const base64url = base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

  return base64url
}

export function handlePushNotificationMessage(message: any, isShowDiaLog: Boolean, successFunc?: Function) {
  let messageObj = JSON.parse(message.body);
  const loading = useLoadingStore();
  if (messageObj.status == PushNotification.IN_PROGRESS) {
    loading.changeLoadingMessage(messageObj.message);
  } else if (messageObj.status == PushNotification.COMPLETED) {
    loading.stopLoading();
    if (isShowDiaLog) {
      if (messageObj.message) {
        const { show } = useMessageDialog();
        show(messageObj.message);
      }
    }

    if (typeof successFunc === 'function') {
      successFunc(messageObj);
    }
    notificationService.unsubscribeNormalSubscriptions();
  }
}

export function handlePushNotificationMessageCallback(message: any, isShowDiaLog: Boolean, successFunc?: Function) {
  let messageObj = JSON.parse(message.body);
  const loading = useLoadingStore();
  if (messageObj.status == PushNotification.IN_PROGRESS) {
    loading.changeLoadingMessage(messageObj.message);
  } else if (messageObj.status == PushNotification.COMPLETED) {
    loading.stopLoading();
    if (isShowDiaLog) {
      const { show } = useMessageDialog();
      show(messageObj.message, () => successFunc?.());
    }

    notificationService.unsubscribeNormalSubscriptions();
  }
}

export function handleLogout(url: string, redirectUrl: string) {
  const popupWindow = window.open(
        url,
        'LogoutPopup',
        `width=1,height=1`
    );

    const intervalId = setInterval(async () => {
        if (popupWindow && popupWindow.closed) {
            clearInterval(intervalId);
            window.location.href = redirectUrl; 
            return;
        }

        if (popupWindow && !popupWindow.location.href.includes('connect/endsession')) {
            await sleep(500);
            popupWindow.close();
            return;
        }
    }, 100);
    
    function sleep(ms:any) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export enum PassThroughFeeType {
  PASS_THROUGH_CLEANING_FEE = 'PASS_THROUGH_CLEANING_FEE',
  PASS_THROUGH_SHORT_TERM_CLEANING_FEE = 'PASS_THROUGH_SHORT_TERM_CLEANING_FEE',
  PASS_THROUGH_LINEN_FEE = 'PASS_THROUGH_LINEN_FEE',
  PASS_THROUGH_COMMUNITY_FEE = 'PASS_THROUGH_COMMUNITY_FEE',
  PASS_THROUGH_MANAGEMENT_FEE = 'PASS_THROUGH_MANAGEMENT_FEE',
  PASS_THROUGH_RESORT_FEE = 'PASS_THROUGH_RESORT_FEE',
  PASS_THROUGH_PET_FEE = 'PASS_THROUGH_PET_FEE',
}